import Image from "next/image";

import { useCallback, useEffect } from "react";

import Swal from "sweetalert2";

import useNotification from "components/Notification/useNotification";
import useAuthentication from "hooks/useAuthentication";
import { fetchApi } from "utils/baseFetcher";

export default function ConnectTwitter() {
  const { account, mutateAccount } = useAuthentication();
  const { setNotification } = useNotification();
  const isConnected = account?.twitterConnected;

  const onDisconnect = useCallback(async () => {
    try {
      const result = await Swal.fire({
        icon: "question",
        title: "Are you sure you want to disconnect your X (Twitter) account?",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        confirmButtonColor: "#ac334b",
        customClass: {
          container: "z-[999999999]",
        },
      });

      if (!result.isConfirmed) return;

      await fetchApi("/api/v2/twitter/unlink_account", {
        method: "POST",
      });

      mutateAccount();
      setNotification(
        "X (Twitter) account disconnected successfully",
        "success"
      );
    } catch (error) {
      setNotification("Failed to disconnect X (Twitter) account", "error");
    }
  }, [mutateAccount, setNotification]);

  const onConnect = useCallback(async () => {
    const response = await fetchApi("/api/v2/twitter/auth");

    window.open(
      response.message,
      "_blank",
      "location=yes,height=570,width=520,scrollbars=yes,status=yes"
    );
  }, []);

  const onClick = useCallback(() => {
    if (isConnected) {
      return onDisconnect();
    }

    onConnect();
  }, [isConnected, onConnect, onDisconnect]);

  const onMessage = useCallback(
    async (event: any) => {
      try {
        const data = JSON.parse(event.data);

        if (data.oauth_token && data.oauth_token_secret) {
          fetchApi("/api/v2/twitter/link_account", {
            method: "POST",
            body: JSON.stringify(data),
          })
            .then(() => {
              mutateAccount();
              setNotification(
                "X (Twitter) account connected successfully",
                "success"
              );
            })
            .catch(() => {
              setNotification("Failed to connect X (Twitter) account", "error");
            });
        }
      } catch (error) {
        console.error(error);
      }
    },
    [mutateAccount, setNotification]
  );

  useEffect(() => {
    window.addEventListener("message", onMessage, false);

    return () => {
      window.removeEventListener("message", onMessage, false);
    };
  }, [onMessage]);

  return (
    <button
      className="flex items-center gap-[10px] bg-black text-white border-0 outline-none p-[10px] rounded-[5px]"
      onClick={onClick}
    >
      <Image
        src={require("assets/x-icon.svg")}
        alt="X"
        height={24}
        width={24}
      />
      {isConnected ? "Disconnect" : "Connect"} X (Twitter)
    </button>
  );
}
